/**
 * DTOs de tasas de comisión por eje — contrato CONTRATO_TASAS_FRONT.md §1.
 *
 * Un endpoint `/tasa/` por nivel (`companies/<id>/tasa/`,
 * `companies/<id>/units/<id>/tasa/`, `merchants/<id>/tasa/`) con el MISMO shape.
 *
 * REGLAS DEL CONTRATO (recordatorios de integración):
 *  - GET: valores string con 2 decimales o `null` (eje sin configurar → UI = 0 %).
 *  - PATCH: body por ejes (`{ tasas: { credito: 3.5, ... } }`), 1 a 4 ejes, valores
 *    número 0-100 con máximo 2 decimales, todo-o-nada (un eje inválido = nada se aplica).
 *  - Solo el GET del COMERCIO trae `sync_pendiente` (cola de sync del micro).
 *  - Solo el PATCH de EMPRESA trae `push_micro` (empuje a la matriz de comisiones).
 */

/**
 * Ejes de comisión. El backend rechaza cualquier otra clave (RN-04).
 * Grafía canónica: `internacional` con 'a' (paralelo a `markup_*_internacional`
 * y `reseller_unit_internacional`; NO usar `_international` con 'i').
 */
export type TasaEje = 'credito' | 'debito' | 'internacional' | 'amex';

/** Los 4 ejes en orden canónico (útil para iterar tarjetas/columnas). */
export const TASA_EJES: readonly TasaEje[] = ['credito', 'debito', 'internacional', 'amex'] as const;

/**
 * Nivel del contrato al que aplica esta tasa. Lo emite el backend en el GET
 * (`extra_data.nivel`) y sirve para saber qué "piso" consultar arriba.
 */
export type TasaNivel = 'empresa' | 'unidad' | 'comercio';

/**
 * Mapa de tasas por eje tal como llega en el GET: string con 2 decimales o `null`.
 * `null` significa "eje sin configurar" — la pantalla lo presenta como 0 % (RN-05).
 * El backend NO persiste un 0 no capturado (violaría el piso del nivel superior).
 */
export type TasasByEje = Partial<Record<TasaEje, string | null>>;

/**
 * GET `/tasa/` — mismo shape en los 3 niveles (venido en `extra_data`).
 * El comercio agrega `sync_pendiente` (cambio de tasa en vuelo hacia el micro).
 */
export interface TasaRead {
  nivel: TasaNivel;
  tasas: TasasByEje;
  /** Solo en GET del COMERCIO. `true` = cambio en vuelo (cola de sync no drenada). */
  sync_pendiente?: boolean;
}

/**
 * Body del PATCH: entre 1 y 4 ejes en número. Los ejes ausentes NO se tocan.
 * Valores 0-100 con máximo 2 decimales (RN-04). Todo-o-nada.
 */
export interface TasaPatchBody {
  tasas: Partial<Record<TasaEje, number>>;
}

/**
 * Un hijo corrido por el diferencial en PATCH (propagación descendente por eje).
 * Cada entrada nombra qué eje se propagó y a qué valor quedó.
 */
export interface TasaPropagado {
  eje: TasaEje;
  /** Identificador del hijo afectado (UUID de UdN o de comercio, según el nivel). */
  id?: string;
  /** Valor final tras propagar el diferencial (porcentaje 0-100). */
  valor?: number;
  /** Cualquier campo adicional que agregue el backend en el futuro. */
  [key: string]: unknown;
}

/**
 * Resultado del PATCH — mismo shape en los 3 niveles. El bloque `push_micro`
 * solo aparece en el PATCH de EMPRESA (empuje a la matriz de comisiones al micro).
 */
export interface TasaPatchResponse {
  tasas_anteriores: TasasByEje;
  tasas: TasasByEje;
  /** Hijos corridos por el diferencial de este PATCH, con `eje` por entrada. */
  propagados: TasaPropagado[];
  /** IDs marcados para sync al micro (drain del beat cada 10 min tras PR #187). */
  sync_marcados: string[];
  /**
   * Estado del empuje inmediato al micro (SOLO en PATCH de empresa).
   * `skipped: true` = empresa sin matriz de comisiones (§4, nota de alta): la
   * captura QUEDÓ; hay que mostrar aviso al usuario para crear la matriz.
   */
  push_micro?: {
    skipped?: boolean;
    reason?: string;
    [key: string]: unknown;
  };
}
